import React, { useState } from "react";
import {
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { format } from "date-fns";
import SectionHeader from "@/components/v2/SectionHeader";
import EmptyState from "@/components/v2/EmptyState";
import TableSkeleton from "@/components/v2/TableSkeleton";
import useExecutionHistory from "@/components/v2/hooks/useExecutionHistory";
import useStrategies from "@/components/v2/hooks/useStrategies";

const statusColors = {
  success: "text-profit",
  failed: "text-loss",
  running: "text-warn",
  pending: "text-muted-foreground",
};

export default function ExecutionHistoryTab() {
  const { runs, loading, error, refetch, filters, setFilters } = useExecutionHistory();
  const { strategies } = useStrategies();
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const runDuration = (run) => {
    if (!run.started_at) return "—";
    const start = new Date(run.started_at).getTime();
    const end = run.completed_at ? new Date(run.completed_at).getTime() : Date.now();
    const ms = end - start;
    const secs = Math.floor(ms / 1000);
    if (secs < 60) return `${secs}s`;
    return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  };

  const getStrategyName = (id) => {
    const s = strategies.find((st) => String(st.id) === String(id));
    return s?.name || id || "—";
  };

  if (error) {
    return (
      <div className="bg-surface-2 border border-border rounded-sm p-6">
        <div className="flex flex-col items-center gap-3 py-8">
          <AlertCircle className="w-8 h-8 text-loss" />
          <p className="text-sm text-muted-foreground">Failed to load execution history</p>
          <Button size="sm" variant="outline" onClick={refetch} className="rounded-sm border-border">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-2 border border-border rounded-sm p-4">
      <SectionHeader
        title="Execution History"
        description="View past strategy executions"
      />

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Status</div>
          <Select
            value={filters.status}
            onValueChange={(v) => setFilters((prev) => ({ ...prev, status: v }))}
          >
            <SelectTrigger className="rounded-sm bg-surface-1 border-border h-8 text-xs w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-surface-2 border-border">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="running">Running</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Strategy</div>
          <Select
            value={filters.strategy_id}
            onValueChange={(v) => setFilters((prev) => ({ ...prev, strategy_id: v }))}
          >
            <SelectTrigger className="rounded-sm bg-surface-1 border-border h-8 text-xs w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-surface-2 border-border">
              <SelectItem value="all">All Strategies</SelectItem>
              {strategies.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="pt-4">
          <Button
            size="sm"
            variant="ghost"
            onClick={refetch}
            className="rounded-sm h-8 text-xs"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <TableSkeleton rows={5} columns={5} />
      ) : runs.length === 0 ? (
        <EmptyState text="No execution history yet. Run a strategy to see results here." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold w-6"></TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Strategy</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Orders</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Started</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Duration</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.map((run) => (
              <Collapsible
                key={run.id}
                open={expandedRows[run.id]}
                onOpenChange={() => toggleRow(run.id)}
                asChild
              >
                <>
                  <TableRow className="border-border cursor-pointer" onClick={() => toggleRow(run.id)}>
                    <TableCell className="p-2">
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-sm">
                          {expandedRows[run.id] ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                    </TableCell>
                    <TableCell className="font-medium text-sm">{getStrategyName(run.strategy_id)}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`text-[10px] font-mono border-border ${statusColors[run.status] || "text-muted-foreground"}`}
                      >
                        {run.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{run.orders_count ?? run.order_count ?? 0}</TableCell>
                    <TableCell>
                      <span className="font-mono text-xs text-muted-foreground">
                        {run.started_at ? format(new Date(run.started_at), "dd MMM HH:mm:ss") : "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs text-muted-foreground">
                        {runDuration(run)}
                      </span>
                    </TableCell>
                  </TableRow>
                  <CollapsibleContent asChild>
                    <TableRow className="border-border">
                      <TableCell colSpan={6} className="bg-surface-1 p-4">
                        <div className="space-y-3">
                          {run.details?.orders && run.details.orders.length > 0 && (
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">
                                Orders
                              </div>
                              <pre className="bg-surface-2 border border-border rounded-sm p-3 text-[11px] font-mono text-foreground overflow-x-auto max-h-40 overflow-y-auto">
                                {JSON.stringify(run.details.orders, null, 2)}
                              </pre>
                            </div>
                          )}
                          {run.details?.log && (
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">
                                Log
                              </div>
                              <pre className="bg-surface-2 border border-border rounded-sm p-3 text-[11px] font-mono text-foreground overflow-x-auto max-h-40 overflow-y-auto">
                                {run.details.log}
                              </pre>
                            </div>
                          )}
                          {run.error && (
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.15em] text-loss font-semibold mb-1">
                                Error
                              </div>
                              <pre className="bg-surface-2 border border-border rounded-sm p-3 text-[11px] font-mono text-loss overflow-x-auto">
                                {run.error}
                              </pre>
                            </div>
                          )}
                          {!run.details?.orders && !run.details?.log && !run.error && (
                            <p className="text-xs text-muted-foreground">No additional details available.</p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  </CollapsibleContent>
                </>
              </Collapsible>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
